import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { formatInZone } from '../../../lib/tripTime';
import type { CreateSeatingChartRequest, SeatingChartSummary } from '../../../types/seating';
import type { GroupDinnerHold } from '../../../types/groups';

/** Sentinel for the select; '' would be indistinguishable from "not chosen yet". */
const NO_DINNER = 'none';

/**
 * The charts for this trip, and the form that makes one.
 *
 * A chart can hang off a dinner hold or stand alone. Bound to a dinner, the
 * people who claimed a spot are the ones to seat and the chart tracks them as
 * claims come and go; unbound, it seats the trip's roster, which is what a
 * welcome reception or a rehearsal table needs. A dinner can only have one live
 * chart — "which one do we set the room from" has to have exactly one answer —
 * so a dinner that already has one is offered as an open, not a create.
 */
export default function SeatingChartList({
  charts,
  dinnerHolds,
  timeZone,
  creating,
  createError,
  onCreate,
  onOpen,
  onArchive,
}: {
  charts: SeatingChartSummary[];
  dinnerHolds: GroupDinnerHold[];
  timeZone: string;
  creating: boolean;
  createError: string;
  onCreate: (body: CreateSeatingChartRequest) => void;
  onOpen: (chartId: string) => void;
  onArchive: (chart: SeatingChartSummary) => void;
}) {
  const [name, setName] = useState('');
  const [dinnerHoldId, setDinnerHoldId] = useState<string>(NO_DINNER);

  const chartByHold = useMemo(() => {
    const map = new Map<string, SeatingChartSummary>();
    for (const chart of charts) {
      if (chart.dinnerHoldId) map.set(chart.dinnerHoldId, chart);
    }
    return map;
  }, [charts]);

  // A cancelled dinner gets no new chart; the API refuses it, and offering it
  // here is offering a button that fails.
  const availableHolds = dinnerHolds.filter((hold) => hold.status !== 'cancelled');
  const canCreate = name.trim().length > 0 && !creating;

  const create = () => {
    if (!canCreate) return;
    // Deliberately does not clear the form. A successful create opens the new
    // chart and unmounts this list; clearing here would only ever run on the
    // path where it hurts — a failed create that threw the name away.
    onCreate({
      name: name.trim(),
      dinnerHoldId: dinnerHoldId === NO_DINNER ? null : dinnerHoldId,
    });
  };

  return (
    <Stack spacing={2}>
      <Paper sx={{ p: 3, borderRadius: 3 }}>
        <Typography variant="h6" fontWeight={800}>Start a seating chart</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
          Lay out the room's tables, then put a name in every chair. Tie it to a dinner and the
          guests who claimed a spot are the ones you seat.
        </Typography>
        {createError ? <Alert severity="error" sx={{ mb: 2 }}>{createError}</Alert> : null}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            fullWidth
            label="Chart name"
            placeholder="Friday dinner"
            value={name}
            onChange={(event) => setName(event.target.value.slice(0, 200))}
          />
          <TextField
            select
            label="Dinner"
            value={dinnerHoldId}
            onChange={(event) => setDinnerHoldId(event.target.value)}
            sx={{ minWidth: { sm: 280 } }}
            helperText={
              dinnerHoldId === NO_DINNER
                ? 'Seats everyone on the trip who has not declined.'
                : 'Seats the guests who claimed a spot at this dinner.'
            }
          >
            <MenuItem value={NO_DINNER}>Not tied to a dinner</MenuItem>
            {availableHolds.map((hold) => (
              <MenuItem key={hold.id} value={hold.id} disabled={chartByHold.has(hold.id)}>
                {hold.venueName}
                {hold.reservedAt ? ` · ${formatInZone(hold.reservedAt, hold.timeZone || timeZone)}` : ''}
                {chartByHold.has(hold.id) ? ' — already has a chart' : ''}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
        <Button variant="contained" sx={{ mt: 2 }} disabled={!canCreate} onClick={create}>
          {creating ? 'Creating…' : 'Create chart'}
        </Button>
      </Paper>

      <Paper sx={{ p: 3, borderRadius: 3 }}>
        <Typography variant="h6" fontWeight={800} sx={{ mb: 2 }}>Seating charts</Typography>
        {charts.length === 0 ? (
          <Typography color="text.secondary">No seating charts yet.</Typography>
        ) : (
          <Stack spacing={1.5}>
            {charts.map((chart) => (
              <Paper key={chart.id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  justifyContent="space-between"
                  alignItems={{ sm: 'center' }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                      <Typography variant="subtitle2" fontWeight={700} noWrap>
                        {chart.name || 'Untitled chart'}
                      </Typography>
                      {/*
                        The badge carries what the state MEANS, because this
                        list is where a host reviews charts they published
                        weeks ago — and publishing did not always show a guest
                        anything. Someone scanning for "what is live" should not
                        have to open each one to find out.
                      */}
                      <Tooltip
                        title={
                          chart.status === 'published'
                            ? 'Guests seated here can see their own table and seat number'
                            : 'Only the hosts can see a draft'
                        }
                      >
                        <Chip
                          size="small"
                          label={chart.status === 'published' ? 'Published' : 'Draft'}
                          color={chart.status === 'published' ? 'success' : 'default'}
                        />
                      </Tooltip>
                      {chart.staleAssignmentCount > 0 ? (
                        <Chip
                          size="small"
                          color="warning"
                          variant="outlined"
                          label={`${chart.staleAssignmentCount} no longer coming`}
                        />
                      ) : null}
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      {chart.dinnerVenueName || 'No dinner attached'} · {chart.seatedCount} of{' '}
                      {chart.candidateCount} seated · {chart.tableCount} table
                      {chart.tableCount === 1 ? '' : 's'}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
                    <Button variant="outlined" size="small" onClick={() => onOpen(chart.id)}>
                      Open
                    </Button>
                    <IconButton
                      size="small"
                      aria-label={`Archive ${chart.name || 'chart'}`}
                      onClick={() => onArchive(chart)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Stack>
              </Paper>
            ))}
          </Stack>
        )}
      </Paper>
    </Stack>
  );
}
